import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Alert, Button, Card, Form, InputNumber, Select, Typography,
} from 'antd';

type ChapterCode = 'at' | 'qu' | 'on' | 'ma' | 'sk' | 'na' | 'sa' | 'bc';

type ChapterOption = {
  value: ChapterCode;
  label: string;
};

type Result = {
  fee: number;
  total: number;
};

const { Paragraph } = Typography;

const Col = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const Emphasis = styled.em`
  color: rgba(11, 18, 32, 0.8);
  font-style: italic;
  font-weight: 700;
`;

const H2 = styled.h2`
  margin: 22px 0 10px;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: -0.01em;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const Step = styled.p`
  margin: 0 0 10px;
  font-size: 16px;
  line-height: 1.75;
  color: rgba(11, 18, 32, 0.86);
`;

const Strong = styled.strong`
  font-weight: 900;
`;

const EstimatorCard = styled(Card)`
  && {
    margin-top: 18px;
    border-radius: 20px;
    border: none;
    background: #505356;
    color: #fff;
    padding: 16px;

    .ant-card-body {
      padding: 22px 22px 18px;
    }

    /* labels */
    .ant-form-item-label > label {
      color: #ffffff;
      font-weight: 800;
      font-size: 18px;
    }

    /* input */
    .ant-select-selector,
    .ant-input-number {
      border-radius: 6px !important;
      border: 1px solid #dededf !important;
    }

    .ant-select-selector {
      height: 48px !important;
      align-items: center !important;
    }

    .ant-input-number {
      height: 48px;
      display: inline-flex;
      align-items: center;
      background: #fff;
    }

    .ant-input-number-input {
      height: 46px !important;
      font-size: 18px !important;
      color: rgba(0, 0, 0, 0.8) !important;
      padding-left: 14px !important;
    }

    /* buttons */
    .ant-btn-primary {
      background: #aa8d6e;
      border-color: #aa8d6e;
      font-weight: 900;
      height: 44px;
      border-radius: 10px;
    }

    .ant-btn-primary:hover {
      background: #9b7f62 !important;
      border-color: #9b7f62 !important;
    }

    .ant-btn-default {
      height: 44px;
      border-radius: 10px;
      font-weight: 800;
    }

    @media (max-width: 767px) {
      .ant-card-body {
        padding: 18px 16px 16px;
      }

      .ant-input-number {
        width: 100% !important;
      }
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const ResultBlock = styled.div`
  margin-top: 26px;
`;

const ResultLabel = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 12px;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const ResultLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 6px 0;

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
`;

const ResultKey = styled.div`
  font-size: 30px;
  font-weight: 900;
  color: #aa8d6e;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const ResultValue = styled.div`
  font-size: 30px;
  font-weight: 900;
  color: #aa8d6e;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const ResultValueWhite = styled.div`
  font-size: 30px;
  font-weight: 900;
  color: #ffffff;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const HintText = styled.div`
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.6;
  font-style: italic;
  color: rgba(255, 255, 255, 0.92);
`;

const InfoBelow = styled(Alert)`
  && {
    margin-top: 18px;
    border-radius: 14px;
  }
`;

const H1 = styled.h1`
  margin: 0 0 14px;
  font-size: 44px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 767px) {
    font-size: 34px;
  }
`;

export const GisEstimator: React.FC = () => {
  const chapterOptions = useMemo<ChapterOption[]>(
    () => [
      { value: 'at', label: 'Atlantic' },
      { value: 'qu', label: 'Quebec' },
      { value: 'on', label: 'Ontario' },
      { value: 'ma', label: 'Manitoba' },
      { value: 'sk', label: 'Saskatchewan' },
      { value: 'na', label: 'Northern Alberta' },
      { value: 'sa', label: 'Southern Alberta' },
      { value: 'bc', label: 'British Columbia' },
    ],
    [],
  );

  const [chapter, setChapter] = useState<ChapterCode | undefined>(undefined);
  const [contractValue, setContractValue] = useState<number | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  const canEnterValue = Boolean(chapter);
  const canCalculate = Boolean(chapter) && typeof contractValue === 'number' && contractValue >= 0;

  const resetAfterChapterChange = () => {
    setContractValue(null);
    setResult(null);
  };

  const formatCurrency = (value: number) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const calculateFee = (value: number, loc: ChapterCode): Result => {
    let gisTotal = 0;

    // Southern AB under 10,000 => 1600
    if (value >= 0 && value < 10000 && loc === 'sa') {
      gisTotal = 1600;

      // Southern AB 10,000-25,000 => 1600
    } else if (value >= 10000 && value < 25000 && loc === 'sa') {
      gisTotal = 1600;

      // Northern AB under 25,000 => 900
    } else if (value >= 0 && value < 25000 && loc === 'na') {
      gisTotal = 900;

      // BC under 25,000 => 900
    } else if (value >= 0 && value < 25000 && loc === 'bc') {
      gisTotal = 900;

      // Atlantic under 25,000 => 1600
    } else if (value >= 0 && value < 25000 && loc === 'at') {
      gisTotal = 1600;

      // Northern AB 25,001-100,000 => 1600
    } else if (value >= 25001 && value < 100000 && loc === 'na') {
      gisTotal = 1600;

      // Any location: 0-100,000 => 1600
    } else if (value >= 0 && value < 100000) {
      gisTotal = 1600;

      // 100,000-500,000 => 1.6%
    } else if (value >= 100000 && value < 500000) {
      gisTotal = value * 0.016;

      // 500,000-1,000,000 => 8000 + (value-500,000)*1%
    } else if (value >= 500000 && value <= 1000000) {
      gisTotal = 8000 + (value - 500000) * 0.01;

      // 1,000,000-1,500,000 => 13000 + (value-1,000,000)*0.5%
    } else if (value >= 1000000 && value <= 1500000) {
      gisTotal = 13000 + (value - 1000000) * 0.005;

      // >= 1,500,000 => 15500 + (value-1,500,000)*0.25%
    } else if (value >= 1500000) {
      gisTotal = 15500 + (value - 1500000) * 0.0025;
    }

    // Original script shows "GIS Fee" and "GIS Total" same (travel part commented out).
    return { fee: gisTotal, total: gisTotal };
  };

  const onCalculate = () => {
    if (!chapter || typeof contractValue !== 'number') return;
    const r = calculateFee(contractValue, chapter);
    setResult(r);
  };

  return (
        <Col>
            <H1>GIS Fee Estimator</H1>
            <Paragraph>
                <Emphasis>
                    PLEASE NOTE: THE TOTAL FEES ARE AN ESTIMATE ONLY. CONTACT YOUR LOCAL CHAPTER OFFICE FOR ANY
                    ADDITIONAL TRAVEL FEES THAT MAY APPLY.
                </Emphasis>
            </Paragraph>

            <H2>How it works:</H2>

            <Step>
                <Strong>STEP 1:</Strong> Select the project’s AWMAC Chapter based on the project’s location.
            </Step>
            <Step>
                <Strong>STEP 2:</Strong> Enter the value of the project’s architectural woodwork{' '}
                <Strong>contract value</Strong>.
            </Step>
            <Step>
                <Strong>STEP 3:</Strong> Select “<Strong>Calculate</Strong>” to view the project’s estimated
                GIS fee.
            </Step>

            <EstimatorCard>
                <Form layout="vertical" onFinish={onCalculate}>
                    <Form.Item label="1. AWMAC Chapter" required>
                        <Select
                            value={chapter}
                            placeholder="--Select--"
                            options={chapterOptions}
                            onChange={(val) => {
                              setChapter(val);
                              resetAfterChapterChange();
                            }}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item label="2. Project Value" required>
                        <InputNumber
                            value={contractValue}
                            onChange={(v) => {
                              setContractValue(typeof v === 'number' ? v : null);
                              setResult(null);
                            }}
                            disabled={!canEnterValue}
                            min={0}
                            step={1}
                            style={{ width: 320 }}
                            size="large"
                            placeholder={canEnterValue ? 'Enter contract value' : 'Select chapter first'}
                            formatter={(v) => {
                              if (v === undefined || v === null) return '';
                              const s = String(v).replace(/[^\d.]/g, '');
                              const [intPart, decPart] = s.split('.');
                              const withSep = (intPart ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                              return decPart ? `${withSep}.${decPart}` : withSep;
                            }}
                            parser={(v) => {
                              if (!v) return 0;
                              const n = Number(String(v).replace(/,/g, ''));
                              return Number.isFinite(n) ? n : 0;
                            }}
                        />
                    </Form.Item>

                    <Actions>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            disabled={!canCalculate}
                        >
                            Calculate
                        </Button>

                        <Button
                            size="large"
                            onClick={() => {
                              setChapter(undefined);
                              setContractValue(null);
                              setResult(null);
                            }}
                        >
                            Reset
                        </Button>
                    </Actions>
                </Form>

                {result && (
                    <ResultBlock>
                        <ResultLabel>The Estimated GIS Fee is:</ResultLabel>

                        <ResultLine>
                            <ResultKey>GIS Fee:</ResultKey>
                            <ResultValue>{formatCurrency(result.fee)}</ResultValue>
                        </ResultLine>

                        <ResultLine>
                            <ResultKey>GIS Total:</ResultKey>
                            <ResultValueWhite>{formatCurrency(result.total)}</ResultValueWhite>
                        </ResultLine>

                        <HintText>
                            PLEASE NOTE: THE TOTAL FEES ARE AN ESTIMATE ONLY. CONTACT YOUR LOCAL CHAPTER OFFICE FOR
                            ANY ADDITIONAL TRAVEL FEES THAT MAY APPLY.
                        </HintText>
                    </ResultBlock>
                )}

                {!result && (
                    <InfoBelow
                        message="Select a chapter and enter a contract value, then click Calculate."
                        type="info"
                        showIcon
                    />
                )}
            </EstimatorCard>
        </Col>
  );
};
